import { useMutation, useQueryClient } from '@tanstack/react-query';
import { remixApi } from '@tectonic/api-client';
import { type FC, type MouseEvent } from 'react';
import { Button } from '../../components';
import { useActionDispatch } from '../../hooks';
import { useHaloScript } from '../../hooks/useHaloScript';
import { queryKeys } from '../../queryKeys';

import type { ElemasonApplyCouponWidget } from '@tectonic/types';
import type { AxiosError } from 'axios';

interface ElemasonApplyCouponWidgetProps {
  widget: ElemasonApplyCouponWidget;
}

const ApplyCouponWidget: FC<ElemasonApplyCouponWidgetProps> = ({
  widget: { data, config, actions },
}) => {
  const queryClient = useQueryClient();
  const dispatch = useActionDispatch();

  const coupon = useHaloScript(data?.coupon);

  const mutation = useMutation({
    mutationFn: () =>
      remixApi.overrideCartDiscounts({
        discountCodes: [coupon!],
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cart() });
    },
    onSuccess: (resp) => {
      actions?.onSuccess?.forEach((action) => dispatch(action, resp));
    },
    onError: (error: AxiosError) => {
      actions?.onError?.forEach((action) => dispatch(action, error));
    },
  });

  const onClick = (e: MouseEvent) => {
    e.stopPropagation();
    mutation.mutate();
  };

  if (!coupon) return null;

  return (
    <Button
      data={data}
      onClick={onClick}
      config={config?.button}
      size={config?.button?.size}
      isLoading={mutation.isPending}
      variant={config?.button?.variant}
      modifier={config?.button?.modifier}
      style={{
        color: config?.button?.text?.color,
        borderColor: config?.button?.borderColor,
        borderRadius: config?.button?.borderRadius,
        backgroundColor: config?.button?.backgroundColor,
      }}
    />
  );
};

export { ApplyCouponWidget };
